import React, { useCallback } from "react"
import { Tooltip } from "react-tooltip"
import { formatCoveredLives } from "./helpers"
import type { SetFormValue } from "./registration_form_component"

const MAX_MARKET_CATEGORIES = 10

export interface IMarketCategoriesMultiSelectProps {
  selected_market_categories: (string|undefined)[]
  available_market_categories: string[]
  covered_lives: (string|undefined)[]
  setFormValue: SetFormValue
  onRemove: () => void
  onAdd: () => void
}

export function MarketCategoriesMultiSelect({
  selected_market_categories,
  available_market_categories,
  covered_lives,
  setFormValue,
  onRemove,
  onAdd,
}: IMarketCategoriesMultiSelectProps) {
  const marketCategoriesNoGaps = selected_market_categories.map((marketCategory) => marketCategory ?? "")

  const marketCategories: string[] = marketCategoriesNoGaps.length === 0 ? [""] : marketCategoriesNoGaps

  return (<>
    {marketCategories.map((marketCategory, index) => {
      const coveredLives = covered_lives[index] ?? ""

      return (
        <MarketCategory
          key={index}
          index={index}
          market_category={marketCategory}
          covered_lives={coveredLives}
          available_market_categories={available_market_categories}
          selected_market_categories={marketCategoriesNoGaps}
          setFormValue={setFormValue}
        />
      )
    })}

    <AddRemoveButtons
      addEnabled={marketCategories.length < MAX_MARKET_CATEGORIES}
      removeEnabled={marketCategories.length > 1}
      onAdd={onAdd}
      onRemove={onRemove}
    />
  </>)
}

function MarketCategory({
  index,
  market_category,
  covered_lives,
  available_market_categories,
  selected_market_categories,
  setFormValue,
}: {
  index: number
  market_category: string
  covered_lives: string
  available_market_categories: string[]
  selected_market_categories: string[]
  setFormValue: SetFormValue
}) {
  return (<>
    {index > 0 && <hr/>}
    <div className="form-group">
      <label className="col-sm-3 control-label required" htmlFor={`insurer_market_categories_${index}`}>
        Market Category &nbsp;
        <i className="fa fa-question-circle" data-tooltip-id={`tip-market-category-${index}`} data-tooltip-content="Select the market categories for which you plan to report data. The Market Category(ies) selected at registration must match the contents of the data submission. Refer to Section 4.4 of the THCE Data Submission Guide for more information on market categories." />
        <Tooltip id={`tip-market-category-${index}`} className="registration-tooltip" />
      </label>
      <div className={"col-sm-6"}>
        <MarketCategorySelect
          index={index}
          market_category={market_category}
          available_market_categories={available_market_categories}
          selected_market_categories={selected_market_categories}
          setFormValue={setFormValue}
        />
      </div>
    </div>
    <div className="form-group">
      <label className="col-sm-3 control-label required" htmlFor={`insurer_market_categories_covered_lives_${index}`}>
        Covered Lives &nbsp;
        <i className="fa fa-question-circle"
          data-tooltip-id={`tip-market-category-covered-lives-${index}`}
          data-tooltip-content="Please report the number of covered lives as of December 31st of the most recent reporting year."
        />
        <Tooltip id={`tip-market-category-covered-lives-${index}`} className="registration-tooltip"/>
      </label>
      <div className={"col-sm-4"}>
        <CoveredLivesInput
          index={index}
          covered_lives={covered_lives}
          setFormValue={setFormValue}
        />
      </div>
    </div>
  </>)
}

function MarketCategorySelect({
  index,
  market_category,
  available_market_categories,
  selected_market_categories,
  setFormValue,
}: {
  index: number
  market_category: string
  available_market_categories: string[]
  selected_market_categories: string[]
  setFormValue: SetFormValue
}) {
  const knownValue = !market_category || available_market_categories.includes(market_category)

  return (
    <select
      name={`insurer[market_categories][${index}]`}
      id={`insurer_market_categories_${index}`}
      value={market_category || ""}
      onChange={(event) => setFormValue(["insurer", "market_categories", index.toString()], event.target.value)}
      className="form-control"
      required={true}
      data-fv-notempty-message="Please select a market category"
    >
      <option />
      {!knownValue && <option value={market_category}>{market_category}</option>}
      {available_market_categories.map((category) => {
        return (
          <option
            key={category}
            value={category}
            disabled={selected_market_categories.includes(category) && category !== market_category}
          >{category}</option>
        )
      })}
    </select>
  )
}

function CoveredLivesInput({
  index,
  covered_lives,
  setFormValue,
}: {
  index: number
  covered_lives: string
  setFormValue: SetFormValue
}) {
  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValue(["insurer", "market_category_covered_lives", index.toString()], event.target.value)
  }, [index, setFormValue])

  const onBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const formatted = formatCoveredLives(event.target.value)
    setFormValue(["insurer", "market_category_covered_lives", index.toString()], formatted)
  }, [index, setFormValue])

  return (
    <input
      type="text"
      name={`insurer[market_category_covered_lives][${index}]`}
      id={`insurer_market_category_covered_lives_${index}`}
      value={covered_lives}
      onChange={onChange}
      onBlur={onBlur}
      className="form-control"
      required={true}
      maxLength={20}
      placeholder="Covered lives"
      aria-label="Covered lives"
    />
  )
}

function AddRemoveButtons({
  addEnabled,
  removeEnabled,
  onAdd,
  onRemove,
}: {
  addEnabled: boolean
  removeEnabled: boolean
  onAdd: () => void
  onRemove: () => void
}) {
  return (
    <div className="form-group hidden-print">
      <AddButton enabled={addEnabled} onClick={onAdd} />
      <RemoveButton enabled={removeEnabled} onClick={onRemove} />
      <div className="col-sm-12">
        <hr style={{margin: "5px 0 10px"}}/>
      </div>
    </div>
  )
}

function AddButton({
  enabled,
  onClick,
}: {
  enabled: boolean
  onClick: () => void
}) {
  return (enabled ? (
    <div className="col-sm-4 col-sm-offset-3">
      <p className="add-another" onClick={onClick}>
        <i className="fa fa-plus-circle fa-lg text-success add-another"/>&nbsp;&nbsp;Add Another Market Category
      </p>
    </div>
  ) : (
    <div className="col-sm-3"/>
  ))
}

function RemoveButton({
  enabled,
  onClick,
}: {
  enabled: boolean
  onClick: () => void
}) {
  return (
    <div className="col-sm-5">
      {enabled && (
        <p className="add-another" onClick={onClick} title="Remove market category">
          <i className="fa fa-minus-circle fa-lg text-danger add-another"/>&nbsp;&nbsp;Remove
        </p>
      )}
    </div>
  )
}
